/**
 * Badge Component
 *
 * A compact component for displaying status, labels, or counts.
 * Follows clean, minimalistic design principles.
 */

import type { BadgeProps } from './badge.types';
import { cn } from '../../lib/cn';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 font-medium transition-all duration-200 whitespace-nowrap',
  {
    variants: {
      variant: {
        default: 'bg-[var(--a-surface-2)] text-[var(--a-text-soft)] border border-[var(--a-line)]',
        primary: 'bg-[var(--a-primary-soft)] text-[var(--a-primary-text)] border border-[var(--a-primary-soft)]',
        secondary: 'bg-[var(--a-surface)] text-[var(--a-text)] border border-[var(--a-line)]',
        success: 'bg-[var(--a-success-soft)] text-[var(--a-success)] border border-[var(--a-success-soft)]',
        warning: 'bg-[var(--a-warning-soft)] text-[var(--a-warning)] border border-[var(--a-warning-soft)]',
        error: 'bg-[var(--a-danger-soft)] text-[var(--a-danger)] border border-[var(--a-danger-soft)]',
        info: 'bg-[var(--a-info-soft)] text-[var(--a-info)] border border-[var(--a-info-soft)]',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs rounded-md',
        md: 'px-2.5 py-1 text-sm rounded-lg',
        lg: 'px-3 py-1.5 text-base rounded-lg',
      },
      dot: {
        true: 'pl-1.5',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      dot: false,
    },
  }
);

const dotVariants = cva('rounded-full', {
  variants: {
    variant: {
      default: 'bg-[var(--a-text-faint)]',
      primary: 'bg-[var(--a-primary)]',
      secondary: 'bg-[var(--a-text-soft)]',
      success: 'bg-[var(--a-success)]',
      warning: 'bg-[var(--a-warning)]',
      error: 'bg-[var(--a-danger)]',
      info: 'bg-[var(--a-info)]',
    },
    size: {
      sm: 'h-1.5 w-1.5',
      md: 'h-2 w-2',
      lg: 'h-2.5 w-2.5',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

const iconSizeMap = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
} as const;

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'lg', leftIcon, rightIcon, dot, children, ...props }, ref) => {
    return (
      <span ref={ref} className={cn(badgeVariants({ variant, size, dot }), className)} {...props}>
        {dot && <span className={cn(dotVariants({ variant, size }))} />}
        {leftIcon && <span className={cn('inline-flex items-center flex-shrink-0', iconSizeMap[size])}>{leftIcon}</span>}
        {children}
        {rightIcon && <span className={cn('inline-flex items-center flex-shrink-0', iconSizeMap[size])}>{rightIcon}</span>}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
